import React, { useState, useEffect, useRef } from 'react';
import styled from 'libs/styled';
import type { MediaItemComponent } from '@fogia/core-web/components/SanityMedia/SanityBackgroundMedia';
import { inBrowser, SANITY_VIDEO_BACKGROUND_POSTER_WIDTH } from '@fogia/core-web/constants';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { getAssetUrlFromReferences } from '@fogia/core-web/utils/getAssetUrlFromReferences';
import { generateSanityImageUrl } from './utils';

/**
 * Props for the SanityBackgroundVideo component
 *
 * @interface SanityBackgroundVideoProps
 */
export interface SanityBackgroundVideoProps {
    /** Sanity video object with file and optional poster (asset references) */
    video: NonNullable<MediaItemComponent['video']>;
    /** Direct video URL. Takes precedence over assets and reference url. */
    videoUrl?: string;
    /** Direct poster image URL. Takes precedence over poster asset resolution. */
    posterUrl?: string;
    /** CSS object-fit value. Defaults to 'cover' */
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    /** Loading strategy. 'lazy' (default), 'eager', 'auto', or 'none'. Maps to video preload attribute ('none' → 'none', 'lazy' → 'metadata', 'eager' → 'auto') */
    loading?: 'lazy' | 'eager' | 'auto' | 'none';
    /** Optional width for the video */
    width?: string | number;
    /** Optional height for the video */
    height?: string | number;
    /** React children to render on top of the video */
    children?: React.ReactNode;
    /** Whether to show video controls. Defaults to false */
    controls?: boolean;
}

const MediaWrapper = styled('div')`
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

const StyledVideo = styled('video')<{ objectFit?: string }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: ${({ objectFit }) => objectFit || 'cover'};
    display: block;
    z-index: 0;
`;

const ContentWrapper = styled('div')`
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
`;

/**
 * Component for rendering Sanity videos as backgrounds with content overlay support.
 *
 * This component renders a semantic `<video>` element positioned absolutely to function as a background.
 * It supports:
 * - Sanity asset references or direct video URLs
 * - Children overlay support (content rendered on top of the video)
 * - Configurable controls and preload behavior
 * - Automatic muted, looped, and autoplay behavior (required for background videos)
 * - Respects `prefers-reduced-motion` for accessibility (disables autoplay when user prefers reduced motion)
 *
 * The video is positioned absolutely with z-index 0, and children are wrapped in a ContentWrapper with z-index 1
 * to ensure proper layering.
 *
 * **Important:** The video is always muted, loops automatically, and autoplays by default. This is required for background videos
 * to work properly across all browsers (especially mobile devices). The `playsInline` attribute ensures the video
 * plays inline on iOS devices instead of entering fullscreen mode.
 *
 * **Accessibility:** The component respects the user's `prefers-reduced-motion` preference. When enabled, autoplay
 * is disabled to prevent motion that could cause discomfort for users with vestibular disorders or motion sensitivity.
 * The video will still be visible (showing the first frame) but will not play automatically. The video element has
 * `aria-hidden="true"` since it is purely decorative and should be ignored by screen readers.
 *
 * @example
 * ```tsx
 * <SanityBackgroundVideo
 *   video={sanityVideoObject}
 *   videoUrl={resolvedUrl}
 *   objectFit="cover"
 *   controls={false}
 *   loading="lazy"
 * >
 *   <h1>Content over video</h1>
 * </SanityBackgroundVideo>
 * ```
 *
 * @returns Rendered video component or null if video URL cannot be generated
 */
const SanityBackgroundVideo: React.FC<SanityBackgroundVideoProps> = ({
    video,
    videoUrl: videoUrlProp,
    posterUrl: posterUrlProp,
    objectFit = 'cover',
    loading = 'lazy',
    width,
    height,
    children,
    controls = false,
    ...rest
}) => {
    const references = (useAppSelector((state) => state.page?.data?.references) ?? {}) as Record<string, unknown>;
    const [shouldAutoplay, setShouldAutoplay] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Respect prefers-reduced-motion for accessibility
    useEffect(() => {
        if (!inBrowser) {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const updateAutoplay = (query: MediaQueryListEvent | MediaQueryList) => {
            setShouldAutoplay(!query.matches);
        };

        updateAutoplay(mediaQuery);

        const cleanups: (() => void)[] = [];

        // Modern browsers support addEventListener
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', updateAutoplay);
            cleanups.push(() => mediaQuery.removeEventListener('change', updateAutoplay));
        }
        // Fallback for older browsers
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy browser support
        else if ((mediaQuery as any).addListener) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy browser support
            (mediaQuery as any).addListener(updateAutoplay);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy browser support
            cleanups.push(() => (mediaQuery as any).removeListener(updateAutoplay));
        }

        return () => {
            cleanups.forEach((fn) => fn());
        };
    }, []);

    // Handle play/pause when shouldAutoplay changes after mount
    useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return;
        }

        if (shouldAutoplay) {
            // Attempt to play (may fail due to browser autoplay policies)
            video.play().catch((error) => {
                // Silently handle play failures in production (e.g., autoplay policy restrictions)
                // Log warning in development for debugging
                if (process.env.NODE_ENV === 'development') {
                    // eslint-disable-next-line no-console -- Development warning for debugging
                    console.warn('SanityBackgroundVideo: Failed to autoplay video', error);
                }
            });
        } else {
            video.pause();
        }
    }, [shouldAutoplay]);

    let videoUrl = videoUrlProp ?? '';

    if (!videoUrl && video.file?.asset) {
        const fileAssetRef = (video.file.asset?.ref ?? '').trim();
        if (!fileAssetRef) return null;
        videoUrl = getAssetUrlFromReferences(references, fileAssetRef) ?? '';
    }

    if (!videoUrl) return null;

    let posterUrl = posterUrlProp ?? '';
    if (!posterUrl && video.poster?.asset) {
        // Use sized poster URL (e.g. w=1440) to avoid loading full-res image (e.g. 7MB+ PNG)
        const sizedPosterUrl = generateSanityImageUrl(
            video.poster as NonNullable<MediaItemComponent['image']>,
            SANITY_VIDEO_BACKGROUND_POSTER_WIDTH,
        );
        if (sizedPosterUrl) {
            posterUrl = sizedPosterUrl;
        } else if (references) {
            const posterAssetRef = video.poster.asset?.ref ?? '';
            const resolvedPosterUrl = getAssetUrlFromReferences(references, posterAssetRef);
            if (resolvedPosterUrl) {
                posterUrl = resolvedPosterUrl;
            }
        }
    }

    return (
        <MediaWrapper {...rest}>
            <StyledVideo
                ref={videoRef}
                muted
                loop
                playsInline
                autoPlay={shouldAutoplay}
                src={videoUrl}
                poster={posterUrl || undefined}
                controls={controls}
                aria-hidden="true"
                tabIndex={-1}
                preload={loading === 'eager' ? 'auto' : loading === 'none' ? 'none' : 'metadata'}
                objectFit={objectFit}
                width={width}
                height={height}
            >
                {/* @TODO: translate this to a string and add it to the translations file */}
                Your browser does not support the video tag.
            </StyledVideo>
            {children && <ContentWrapper>{children}</ContentWrapper>}
        </MediaWrapper>
    );
};

export default SanityBackgroundVideo;
